import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  valueForOnlyNumericInputTextFields,
  valueForOnlyStringInputTextFields,
} from "utils/helpers";

import {
  selectBackWallTypeForm,
  selectBackWallTypeSaving,
  selectCanSubmitBackWallType,
  selectIsAddModalOpen,
} from "../../redux/selector";
import { backWallTypeActions } from "../../redux/slice";
import { BackWallTypeForm, FieldType, FieldValues } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof BackWallTypeForm;
  label: string;
};

const isNumberField = (name: keyof BackWallTypeForm) =>
  FieldValues[name] === FieldType.Number;

const normalizeBackWallTypeFieldValue = (
  name: keyof BackWallTypeForm,
  value: string,
) => {
  if (isNumberField(name)) {
    return valueForOnlyNumericInputTextFields(value);
  }
  return valueForOnlyStringInputTextFields(value);
};

const FIELDS: FieldConfig[] = [
  { key: "backWallCode", label: "CODE" },
  { key: "backWallDesc", label: "DESCRIPTION" },
  { key: "bump", label: "PROTRUSION" },
];

const BACK_WALL_TYPE_MODAL = "BACK_WALL_TYPE.MODAL";

const AddBackWallTypeModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectBackWallTypeForm);
  const saving = useSelector(selectBackWallTypeSaving);
  const canSubmit = useSelector(selectCanSubmitBackWallType);

  const onClose = () => {
    if (saving) return;
    dispatch(backWallTypeActions.closeAddModal());
  };

  const setField = (name: keyof BackWallTypeForm, value: string) =>
    dispatch(
      backWallTypeActions.setFormField({
        name,
        value: normalizeBackWallTypeFieldValue(name, value),
      }),
    );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>
            {t(`${BACK_WALL_TYPE_MODAL}.ADD_TITLE`)}
          </Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FIELDS.map(({ key, label }) => (
            <Box key={key} className={styles.field}>
              <Typography className={styles.label}>
                {t(`${BACK_WALL_TYPE_MODAL}.FIELDS.${label}`)}
              </Typography>
              <TextField
                className={styles.input}
                fullWidth
                variant="outlined"
                placeholder={t(
                  `${BACK_WALL_TYPE_MODAL}.PLACEHOLDERS.ENTER_${label}`,
                )}
                value={form[key]}
                disabled={saving}
                InputProps={{ notched: false }}
                {...(isNumberField(key) ? { inputMode: "numeric" as const } : {})}
                onChange={(event) => setField(key, event.target.value)}
              />
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!canSubmit || saving}
            onClick={() => dispatch(backWallTypeActions.addBackWallType())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddBackWallTypeModal;
